/**
 * Who may use the support portal.
 *
 * Access to everything support-related is gated on one thing: the signed-in user
 * has an `@gondola.ai` email. Google Workspace controls who has one, so the
 * domain is the allowlist — there is no separate operator table.
 *
 * This mirrors the backend gate in `api_support/lib/operator_auth.is_support_email`
 * exactly: split on the LAST `@` and compare the domain literally, so lookalikes
 * (`someone@evil-gondola.ai`, `someone@gondola.ai.attacker.com`,
 * `someone@sub.gondola.ai`) do not pass. The frontend gate is a UX affordance;
 * `api_support` re-checks the same rule on every call, so a bypass here still
 * can't reach data.
 */
export const SUPPORT_EMAIL_DOMAIN = "gondola.ai";

/**
 * gondola.ai addresses that must NOT get operator access despite the domain.
 *
 * `testy.mctesterson@gondola.ai` is a shared test-credential account:
 * `gondola-mobile-app/eas.json` ships it in `EXPO_PUBLIC_TEST_USER_EMAILS`
 * (including the production profile), and the web test-signin route mints a
 * prod-signed session for it. It is on the gondola.ai domain, so the plain
 * domain gate would make it an operator — a shared-password account able to see
 * customer PII and (later) move money. Deny it explicitly here and in
 * `api_support` before write actions land.
 */
export const SUPPORT_OPERATOR_DENYLIST: ReadonlySet<string> = new Set([
  "testy.mctesterson@gondola.ai",
]);

export function isSupportOperatorEmail(
  email: string | null | undefined
): boolean {
  if (!email || !email.includes("@")) return false;
  const normalized = email.trim().toLowerCase();
  if (SUPPORT_OPERATOR_DENYLIST.has(normalized)) return false;
  const domain = normalized.split("@").pop();
  return domain === SUPPORT_EMAIL_DOMAIN;
}
